import { Link } from 'react-router-dom'
import type { OrgBillingSummary } from '../api'
import { dateOnly, planLabel } from '../format'

const TRIAL_DAYS = 14
const MS_PER_DAY = 86_400_000

function trialCountdown(trialEnd: string, trialStart?: string): { daysLeft: number; pctRemaining: number } | null {
  const end = new Date(trialEnd)
  if (Number.isNaN(end.getTime())) return null

  const endMs = end.getTime()
  const startMs = trialStart ? new Date(trialStart).getTime() : endMs - TRIAL_DAYS * MS_PER_DAY
  if (Number.isNaN(startMs)) return null

  const totalMs = endMs - startMs
  if (totalMs <= 0) return null

  const remainingMs = Math.max(0, endMs - Date.now())
  const daysLeft = Math.ceil(remainingMs / MS_PER_DAY)
  const pctRemaining = Math.min(100, Math.max(0, Math.round((remainingMs / totalMs) * 100)))

  return { daysLeft, pctRemaining }
}

function daysLeftLabel(daysLeft: number): string {
  if (daysLeft <= 0) return 'Ends today'
  if (daysLeft === 1) return '1 day left'
  return `${daysLeft} days left`
}

/** Shown while an organization is trialing so admins know when billing starts. */
export function TrialBanner({ billing, orgName }: { billing: OrgBillingSummary; orgName: string }) {
  const sub = billing.subscription
  if (!sub || sub.status !== 'trialing') return null

  const trialEndLabel = sub.trial_end ? dateOnly(sub.trial_end) : null
  const countdown = sub.trial_end ? trialCountdown(sub.trial_end, sub.created_at) : null

  return (
    <div className="billing-banner-wrap">
      <div className="billing-banner" role="status">
        <div className="billing-banner-copy">
          <span className="billing-banner-badge">Trial</span>
          <div className="billing-banner-text">
            <p className="billing-banner-title">
              {planLabel(sub.plan)} for {orgName}
              {trialEndLabel ? <span className="muted"> · ends {trialEndLabel}</span> : null}
            </p>
            <p className="billing-banner-sub muted">
              Add a payment method before the trial ends to keep sending.
            </p>
          </div>
        </div>

        {countdown && (
          <div className="billing-banner-countdown" aria-label="Trial time remaining">
            <span className="billing-banner-countdown-label">Days left</span>
            <span className="billing-banner-countdown-value mono">{daysLeftLabel(countdown.daysLeft)}</span>
            <div className="billing-banner-countdown-bar" aria-hidden>
              <div
                className="billing-banner-countdown-fill"
                style={{ width: `${countdown.pctRemaining}%` }}
              />
            </div>
          </div>
        )}

        <Link className="btn btn-secondary btn-small billing-banner-cta" to={`/orgs/${billing.organization_id}/billing`}>
          Billing
        </Link>
      </div>
    </div>
  )
}
